import { type FormEvent, useState } from "react"
import { Wordmark } from "../components/brand"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { Button, ErrorNote, Field, Spinner, useLoad } from "../components/ui"
import { type Company, type Employee, api } from "../lib/api"
import { useAuth } from "../lib/auth"

// The five unauthenticated pages. They share a frame and a submit shape:
// local state, a submitting flag, try/catch, error rendered inline.

function AuthFrame({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
  footer?: React.ReactNode
}) {
  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center gap-1.5 text-center">
          <Wordmark className="h-5 w-auto text-foreground" />
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
          {subtitle && <p className="text-sm text-muted">{subtitle}</p>}
        </div>
        <div className="rounded-lg border border-border bg-surface p-6">{children}</div>
        {footer && <div className="mt-4 text-center text-sm text-muted">{footer}</div>}
      </div>
    </div>
  )
}

// Every form here does the same three things around a request. Sharing it keeps
// a page from forgetting to clear the error or reset the flag on failure.
function useSubmit(action: (event: FormEvent<HTMLFormElement>) => Promise<void>) {
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  return {
    error,
    busy,
    onSubmit: async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setError(null)
      setBusy(true)
      try {
        await action(event)
      } catch (err) {
        setError(err)
      } finally {
        setBusy(false)
      }
    },
  }
}

type AuthResponse = { jwt: string; employee: Employee; company: Company }

export function LoginPage() {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  // Arriving from an accepted invitation: the account already existed, so the
  // invitation added the company and the person now signs in as themselves.
  const [params] = useSearchParams()
  const invited = params.get("invited")
  const { error, busy, onSubmit } = useSubmit(async (event) => {
    const form = new FormData(event.currentTarget)
    const res = await api.post<AuthResponse>("/auth/login", {
      email: form.get("email"),
      password: form.get("password"),
    })
    await signIn(res.jwt)
    navigate("/", { replace: true })
  })

  return (
    <AuthFrame
      title="Sign in"
      subtitle={invited ? `Invitation accepted. Sign in to continue to ${invited}.` : undefined}
      footer={
        <>
          No account? <Link to="/signup" className="text-interactive hover:underline">Create one</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          defaultValue={params.get("email") ?? undefined}
          required
        />
        <Field label="Password" name="password" type="password" autoComplete="current-password" required />
        <ErrorNote error={error} />
        <Button type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        <Link to="/forgot-password" className="text-center text-sm text-muted hover:text-foreground">
          Forgot your password?
        </Link>
      </form>
    </AuthFrame>
  )
}

export function SignupPage() {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const { error, busy, onSubmit } = useSubmit(async (event) => {
    const form = new FormData(event.currentTarget)
    const res = await api.post<AuthResponse>("/auth/register", {
      companyName: form.get("companyName"),
      domain: form.get("domain"),
      name: form.get("name"),
      email: form.get("email"),
      password: form.get("password"),
    })
    await signIn(res.jwt)
    navigate("/", { replace: true })
  })

  return (
    <AuthFrame
      title="Create your company"
      subtitle="You get $10 of free credits to try it with."
      footer={
        <>
          Already have an account? <Link to="/login" className="text-interactive hover:underline">Sign in</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label="Company name" name="companyName" required />
        <Field label="Company domain" name="domain" placeholder="acme.com" required />
        <Field label="Your name" name="name" required />
        <Field label="Email" name="email" type="email" autoComplete="email" required />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          hint="At least 8 characters."
          required
        />
        <ErrorNote error={error} />
        <Button type="submit" disabled={busy}>
          {busy ? "Creating…" : "Create company"}
        </Button>
      </form>
    </AuthFrame>
  )
}

export function AcceptInvitePage() {
  const [params] = useSearchParams()
  const token = params.get("token") ?? ""
  const { signIn } = useAuth()
  const navigate = useNavigate()

  // Whether the person already has an account decides the form — someone who
  // has one keeps their name and password, so there is nothing to fill in.
  // Asked before rendering rather than after submitting, so nobody types a
  // password that would go nowhere.
  const context = useLoad(
    () =>
      token
        ? api.get<{ email: string; companyName: string; needsSetup: boolean }>(
            `/auth/accept-invite?token=${encodeURIComponent(token)}`,
          )
        : Promise.resolve(null),
    [token],
  )

  const { error, busy, onSubmit } = useSubmit(async (event) => {
    const form = new FormData(event.currentTarget)
    const res = await api.post<AuthResponse | { needsLogin: true; email: string }>("/auth/accept-invite", {
      token,
      // Only a first-time account is setting credentials; for an existing one
      // the fields are not even on the page.
      ...(context.data?.needsSetup
        ? { name: form.get("name"), password: form.get("password") }
        : {}),
    })
    // An existing account is added to the company but not signed in: the link
    // proves the email arrived, not who is holding it. They sign in as usual.
    if ("needsLogin" in res) {
      const query = new URLSearchParams({ email: res.email, invited: context.data?.companyName ?? "the company" })
      navigate(`/login?${query}`, { replace: true })
      return
    }
    await signIn(res.jwt)
    navigate("/", { replace: true })
  })

  if (!token) {
    return (
      <AuthFrame title="Accept your invitation">
        <p className="text-sm text-muted">
          This link is missing its token. Open the link from your invitation email exactly as it was sent.
        </p>
      </AuthFrame>
    )
  }

  if (context.loading) {
    return (
      <AuthFrame title="Accept your invitation">
        <Spinner />
      </AuthFrame>
    )
  }

  if (context.error) {
    return (
      <AuthFrame
        title="Accept your invitation"
        footer={<Link to="/login" className="text-interactive hover:underline">Back to sign in</Link>}
      >
        <p className="text-sm text-muted">
          This invitation is no longer valid — it has expired, was already accepted, or the link was altered.
        </p>
      </AuthFrame>
    )
  }

  const needsSetup = context.data?.needsSetup ?? false
  const companyName = context.data?.companyName ?? "the company"

  return (
    <AuthFrame
      title="Accept your invitation"
      subtitle={needsSetup ? `Choose a name and password to join ${companyName}.` : `${companyName} invited you.`}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {needsSetup ? (
          <>
            <Field label="Your name" name="name" required />
            <Field
              label="Password"
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </>
        ) : (
          // The account and its password already exist. Accepting only adds
          // this company to their list, so there is nothing to type — and
          // saying so beats asking for a password that would be discarded.
          <p className="text-sm text-muted">
            You already have a Moderniser account. Accepting adds{" "}
            <strong className="text-foreground">{companyName}</strong> to your companies; then sign in with
            your existing password.
          </p>
        )}
        <ErrorNote error={error} />
        <Button type="submit" disabled={busy}>
          {busy ? "Accepting…" : "Accept invitation"}
        </Button>
      </form>
    </AuthFrame>
  )
}

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false)
  const { error, busy, onSubmit } = useSubmit(async (event) => {
    const form = new FormData(event.currentTarget)
    await api.post("/auth/forgot-password", { email: form.get("email") })
    setSent(true)
  })

  return (
    <AuthFrame
      title="Reset your password"
      footer={<Link to="/login" className="text-interactive hover:underline">Back to sign in</Link>}
    >
      {sent ? (
        // Deliberately says "if that address has an account". The API answers
        // identically either way so the page must not reveal more than it does.
        <p className="text-sm text-muted">
          If that address has an account, a reset link is on its way. It expires in an hour.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Field label="Email" name="email" type="email" autoComplete="email" required />
          <ErrorNote error={error} />
          <Button type="submit" disabled={busy}>
            {busy ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
    </AuthFrame>
  )
}

export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const token = params.get("token") ?? ""
  const navigate = useNavigate()
  const [done, setDone] = useState(false)
  const { error, busy, onSubmit } = useSubmit(async (event) => {
    const form = new FormData(event.currentTarget)
    await api.post("/auth/reset-password", { token, password: form.get("password") })
    setDone(true)
    setTimeout(() => navigate("/login", { replace: true }), 1500)
  })

  if (!token) {
    return (
      <AuthFrame title="Set a new password">
        <p className="text-sm text-muted">
          This link is missing its token. Open the link from your reset email exactly as it was sent.
        </p>
      </AuthFrame>
    )
  }

  return (
    <AuthFrame title="Set a new password">
      {done ? (
        <p className="text-sm text-success">Password changed. Taking you to sign in…</p>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Field
            label="New password"
            name="password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            required
          />
          <ErrorNote error={error} />
          <Button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Change password"}
          </Button>
        </form>
      )}
    </AuthFrame>
  )
}
